import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WEB_ROUTES } from '@wonderherbals/shared';
import { useUIStore } from '../stores/useUIStore';
import { useCartStore } from '../stores/useCartStore';
import { useAuthStore } from '../stores/useAuthStore';

// Fresh Leaf navigation: a frosted-glass pill with segmented links and a leaf
// Bag button. The announcement bar scrolls away; the pill follows it up and then
// pins 12px from the top, hiding while you scroll down and returning on the way up.

const ANNOUNCE_H = 40;
const PILL_GAP = 24;   // pill offset below the announcement bar
const PINNED_TOP = 12; // pill offset once the bar has scrolled away

function useNavScroll() {
  const [state, setState] = useState({ scrollY: 0, hidden: false });
  const lastScrollY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setState({ scrollY: y, hidden: y > 160 && y > lastScrollY.current + 8 });
      lastScrollY.current = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return state;
}

// ── Icons ──────────────────────────────────────────────────────────────────────
const IcoSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5 20 20" />
  </svg>
);
const IcoBag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 8h14l-1 12H6L5 8Z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);
const IcoUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" /><path d="M4 20c1.5-4 4.5-6 8-6s6.5 2 8 6" />
  </svg>
);
const IcoMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M4 9h16M4 15h16" />
  </svg>
);
const IcoX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// ── Announcement bar ───────────────────────────────────────────────────────────
const MESSAGES = [
  'Free shipping on orders above ₹999',
  '100% natural · Zero chemicals · AYUSH approved',
  'Stone-crushed in Hyderabad since 2009',
];

function AnnouncementBar() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      role="region"
      aria-label="Announcements"
      style={{
        height: ANNOUNCE_H, background: '#0F2616',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35 }}
          style={{ fontSize: 12.5, fontWeight: 600, color: '#CFE3BC', margin: 0 }}
        >
          <span aria-hidden="true" style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: '#8BC540', marginRight: 10, verticalAlign: 1 }} />
          {MESSAGES[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// ── Nav links ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  { label: 'Home',        href: WEB_ROUTES.HOME },
  { label: 'Shop',        href: WEB_ROUTES.SHOP },
  { label: 'Ingredients', href: WEB_ROUTES.INGREDIENTS },
  { label: 'Prakriti',    href: WEB_ROUTES.DOSHA_QUIZ },
  { label: 'Seasons',     href: '/ritucharya' },
  { label: 'Our story',   href: WEB_ROUTES.AYURVEDA },
];

/** The single nav link that best matches the path (longest matching prefix). */
function activeHref(pathname: string): string | null {
  let best: string | null = null;
  for (const { href } of NAV_LINKS) {
    const hit = href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/');
    if (hit && (!best || href.length > best.length)) best = href;
  }
  return best;
}

const circleBtn: React.CSSProperties = {
  width: 44, height: 44, borderRadius: '50%',
  border: 0, background: 'rgba(255,255,255,0.8)', color: '#0F2616',
  alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', textDecoration: 'none', flexShrink: 0,
};

// ── Main Navbar ────────────────────────────────────────────────────────────────
export default function Navbar() {
  const location = useLocation();
  const { scrollY, hidden } = useNavScroll();
  const { openCart, openSearch } = useUIStore();
  const itemCount = useCartStore((s) => s.itemCount());
  const { session } = useAuthStore();

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const top = Math.max(PINNED_TOP, ANNOUNCE_H + PILL_GAP - scrollY);
  const lifted = scrollY > ANNOUNCE_H;

  return (
    <>
      <AnnouncementBar />

      <motion.div
        style={{
          position: 'fixed',
          left: 0, right: 0,
          top,
          zIndex: 50,
          paddingInline: 'clamp(12px, 2.5vw, 36px)',
          pointerEvents: 'none',
        }}
        animate={{ y: hidden && !mobileOpen ? '-140%' : 0 }}
        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      >
        <header
          className="glass"
          style={{
            pointerEvents: 'auto',
            maxWidth: 1368,
            margin: '0 auto',
            height: 68,
            borderRadius: 9999,
            boxSizing: 'border-box',
            padding: '0 6px 0 clamp(14px, 1.6vw, 22px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            boxShadow: lifted ? '0 12px 40px rgba(15,38,22,0.14)' : '0 8px 30px rgba(15,38,22,0.08)',
            transition: 'box-shadow 0.3s',
          }}
        >
          <Link to="/" aria-label="Wonder Herbals home" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src="/images/fresh-leaf/logo-green.png" alt="Wonder Herbals, established 2009" className="h-6 sm:h-7" style={{ width: 'auto', display: 'block' }} />
          </Link>

          <nav
            aria-label="Main"
            className="hidden lg:flex"
            style={{ alignItems: 'center', gap: 2, padding: 4, borderRadius: 9999, background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.9)' }}
          >
            {NAV_LINKS.map(link => {
              const active = activeHref(location.pathname) === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={active ? 'page' : undefined}
                  className="transition-colors hover:bg-white/70"
                  style={{
                    height: 44, padding: '0 18px', borderRadius: 9999,
                    display: 'flex', alignItems: 'center',
                    fontSize: 14, fontWeight: active ? 700 : 600,
                    color: '#0F2616', textDecoration: 'none', whiteSpace: 'nowrap',
                    background: active ? '#FFFFFF' : undefined,
                    boxShadow: active ? '0 2px 10px rgba(15,38,22,0.08)' : 'none',
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button type="button" onClick={openSearch} aria-label="Search" className="flex" style={circleBtn}>
              <IcoSearch />
            </button>
            <Link
              to={session ? WEB_ROUTES.ACCOUNT : WEB_ROUTES.LOGIN}
              aria-label={session ? 'My account' : 'Sign in'}
              className="hidden sm:flex"
              style={circleBtn}
            >
              <IcoUser />
            </Link>
            <button
              type="button"
              onClick={openCart}
              aria-label={`Bag, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
              style={{
                height: 52, padding: '0 6px 0 16px', borderRadius: 9999,
                border: 0, background: '#8BC540', color: '#0F2616',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <IcoBag />
              <span className="hidden sm:inline">Bag</span>
              <span style={{
                minWidth: 40, height: 40, padding: '0 8px', boxSizing: 'border-box', borderRadius: 20,
                background: '#0F2616', color: '#FFFFFF', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(o => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              className="flex lg:hidden"
              style={circleBtn}
            >
              {mobileOpen ? <IcoX /> : <IcoMenu />}
            </button>
          </div>
        </header>

        {/* ── Mobile menu: a glass sheet under the pill ─────────────────────── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              aria-label="Main"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="glass lg:hidden"
              style={{
                pointerEvents: 'auto',
                marginTop: 8,
                borderRadius: 30,
                padding: 10,
                display: 'flex', flexDirection: 'column', gap: 4,
                boxShadow: '0 20px 60px rgba(15,38,22,0.18)',
              }}
            >
              {NAV_LINKS.map(link => {
                const active = activeHref(location.pathname) === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={active ? 'page' : undefined}
                    style={{
                      height: 52, padding: '0 18px', borderRadius: 20,
                      display: 'flex', alignItems: 'center',
                      fontSize: 16, fontWeight: 700, color: '#0F2616', textDecoration: 'none',
                      background: active ? '#FFFFFF' : 'transparent',
                    }}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to={session ? WEB_ROUTES.ACCOUNT : WEB_ROUTES.LOGIN}
                style={{
                  marginTop: 6, height: 52, borderRadius: 9999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#0F2616', color: '#FFFFFF',
                  fontSize: 15, fontWeight: 700, textDecoration: 'none',
                }}
              >
                {session ? 'My account' : 'Sign in'}
              </Link>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
